// src/services/features/fse/sessionSlice.js
// FSE tracking sessions — powers the web "FSE Tracking" page.
// Hits the same backend endpoints the mobile app uses (see app's
// src/services/features/fse/sessionSlice.js and
// src/screens/FieldSalesExecutive/{RouteList,FSETracking,MapScreen}.js),
// so no backend changes are required to surface this on the web.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../API/api';

// ─── FETCH ALL (paginated) ───────────────────────────────────────────────────
export const fetchAllSessions = createAsyncThunk(
  'session/fetchAll',
  async ({ page = 1, limit = 50, append = false, ...filters } = {}, { rejectWithValue }) => {
    try {
      // filters: from (ISO), to, role, search, userId, status — resolved on the server
      const res = await API.get('/api/session', { params: { page, limit, ...filters } });
      // Handle both { sessions: [...] } and plain [...] response shapes.
      const sessions = Array.isArray(res.data) ? res.data : (res.data?.sessions ?? []);
      const pagination = res.data?.pagination ?? {
        page,
        limit,
        total: sessions.length,
        totalPages: 1,
      };
      return {
        sessions,
        pagination,
        append,
        stats: res.data?.stats ?? null,   // liveNow / sessionsToday / distanceTodayKm / people
        people: res.data?.people ?? null, // everyone the viewer may see (for the filter)
      };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Network error. Please try again.',
      );
    }
  },
);

// ─── FETCH NEXT PAGE (Load more) ─────────────────────────────────────────────
export const fetchNextSessionsPage = createAsyncThunk(
  'session/fetchNextPage',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const { session } = getState();
    const { pagination } = session;
    if (!pagination || pagination.page >= pagination.totalPages) {
      return rejectWithValue('No more pages');
    }
    return dispatch(
      fetchAllSessions({ page: pagination.page + 1, limit: pagination.limit, append: true }),
    ).unwrap();
  },
);

// ─── FETCH BY ID (session detail — route points, distance, etc.) ────────────
export const fetchSessionById = createAsyncThunk(
  'session/fetchById',
  async (sessionId, { rejectWithValue }) => {
    try {
      const res = await API.get(`/api/session/${sessionId}`);
      return res.data?.session ?? res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Network error. Please try again.',
      );
    }
  },
);

// ─── FORCE REBUILD (server recomputes route/distance from raw points) ───────
export const rebuildSession = createAsyncThunk(
  'session/rebuild',
  async (sessionId, { rejectWithValue }) => {
    try {
      await API.post(`/api/session/rebuild/${sessionId}`);
      const res = await API.get(`/api/session/${sessionId}`);
      return res.data?.session ?? res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to refresh route.',
      );
    }
  },
);

const sessionSlice = createSlice({
  name: 'session',
  initialState: {
    list: [],
    selected: null,
    listState: 'idle',   // idle | loading | success | error
    detailState: 'idle', // idle | loading | success | error
    loading: false,
    loadingMore: false,
    error: null,
    pagination: { page: 1, limit: 50, total: 0, totalPages: 1 },
    stats: null,
    people: [],
  },
  reducers: {
    clearSelectedSession(state) {
      state.selected = null;
      state.detailState = 'idle';
    },
    clearSessionError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchAllSessions
      .addCase(fetchAllSessions.pending, (state, action) => {
        const isAppend = action.meta.arg?.append;
        if (isAppend) state.loadingMore = true;
        else {
          state.listState = 'loading';
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchAllSessions.fulfilled, (state, action) => {
        const { sessions, pagination, append, stats, people } = action.payload;
        state.listState = 'success';
        state.loading = false;
        state.loadingMore = false;
        state.list = append ? [...state.list, ...sessions] : sessions;
        state.pagination = pagination;
        if (stats) state.stats = stats;
        if (people) state.people = people;
      })
      .addCase(fetchAllSessions.rejected, (state, action) => {
        state.listState = 'error';
        state.loading = false;
        state.loadingMore = false;
        state.error = action.payload;
      })

      // fetchSessionById
      .addCase(fetchSessionById.pending, (state) => {
        state.detailState = 'loading';
        state.error = null;
      })
      .addCase(fetchSessionById.fulfilled, (state, action) => {
        state.detailState = 'success';
        state.selected = action.payload;
      })
      .addCase(fetchSessionById.rejected, (state, action) => {
        state.detailState = 'error';
        state.error = action.payload;
      })

      // rebuildSession
      .addCase(rebuildSession.pending, (state) => {
        state.detailState = 'loading';
      })
      .addCase(rebuildSession.fulfilled, (state, action) => {
        state.detailState = 'success';
        state.selected = action.payload;
      })
      .addCase(rebuildSession.rejected, (state, action) => {
        state.detailState = 'error';
        state.error = action.payload;
      });
  },
});

export const { clearSelectedSession, clearSessionError } = sessionSlice.actions;
export default sessionSlice.reducer;

//---------- 13.10.2026 Backup -----------------
// // src/services/features/fse/sessionSlice.js
// // FSE tracking sessions — powers the web "FSE Tracking" page.
// // Hits the same backend endpoints the mobile app uses (see app's
// // src/services/features/fse/sessionSlice.js and
// // src/screens/FieldSalesExecutive/{RouteList,FSETracking,MapScreen}.js),
// // so no backend changes are required to surface this on the web.
// import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
// import API from '../../API/api';

// // ─── FETCH ALL (paginated) ───────────────────────────────────────────────────
// export const fetchAllSessions = createAsyncThunk(
//   'session/fetchAll',
//   async ({ page = 1, limit = 50, append = false } = {}, { rejectWithValue }) => {
//     try {
//       const res = await API.get('/api/session', { params: { page, limit } });
//       // Handle both { sessions: [...] } and plain [...] response shapes.
//       const sessions = Array.isArray(res.data) ? res.data : (res.data?.sessions ?? []);
//       const pagination = res.data?.pagination ?? {
//         page,
//         limit,
//         total: sessions.length,
//         totalPages: 1,
//       };
//       return { sessions, pagination, append };
//     } catch (err) {
//       return rejectWithValue(
//         err.response?.data?.message || err.message || 'Network error. Please try again.',
//       );
//     }
//   },
// );

// // ─── FETCH NEXT PAGE (Load more) ─────────────────────────────────────────────
// export const fetchNextSessionsPage = createAsyncThunk(
//   'session/fetchNextPage',
//   async (_, { getState, dispatch, rejectWithValue }) => {
//     const { session } = getState();
//     const { pagination } = session;
//     if (!pagination || pagination.page >= pagination.totalPages) {
//       return rejectWithValue('No more pages');
//     }
//     return dispatch(
//       fetchAllSessions({ page: pagination.page + 1, limit: pagination.limit, append: true }),
//     ).unwrap();
//   },
// );

// // ─── FETCH BY ID (session detail — route points, distance, etc.) ────────────
// export const fetchSessionById = createAsyncThunk(
//   'session/fetchById',
//   async (sessionId, { rejectWithValue }) => {
//     try {
//       const res = await API.get(`/api/session/${sessionId}`);
//       return res.data?.session ?? res.data;
//     } catch (err) {
//       return rejectWithValue(
//         err.response?.data?.message || err.message || 'Network error. Please try again.',
//       );
//     }
//   },
// );

// // ─── FORCE REBUILD (server recomputes route/distance from raw points) ───────
// export const rebuildSession = createAsyncThunk(
//   'session/rebuild',
//   async (sessionId, { rejectWithValue }) => {
//     try {
//       await API.post(`/api/session/rebuild/${sessionId}`);
//       const res = await API.get(`/api/session/${sessionId}`);
//       return res.data?.session ?? res.data;
//     } catch (err) {
//       return rejectWithValue(
//         err.response?.data?.message || err.message || 'Failed to refresh route.',
//       );
//     }
//   },
// );

// const sessionSlice = createSlice({
//   name: 'session',
//   initialState: {
//     list: [],
//     selected: null,
//     listState: 'idle',   // idle | loading | success | error
//     detailState: 'idle', // idle | loading | success | error
//     loading: false,
//     loadingMore: false,
//     error: null,
//     pagination: { page: 1, limit: 50, total: 0, totalPages: 1 },
//   },
//   reducers: {
//     clearSelectedSession(state) {
//       state.selected = null;
//       state.detailState = 'idle';
//     },
//     clearSessionError(state) {
//       state.error = null;
//     },
//   },
//   extraReducers: (builder) => {
//     builder
//       // fetchAllSessions
//       .addCase(fetchAllSessions.pending, (state, action) => {
//         const isAppend = action.meta.arg?.append;
//         if (isAppend) state.loadingMore = true;
//         else {
//           state.listState = 'loading';
//           state.loading = true;
//         }
//         state.error = null;
//       })
//       .addCase(fetchAllSessions.fulfilled, (state, action) => {
//         const { sessions, pagination, append } = action.payload;
//         state.listState = 'success';
//         state.loading = false;
//         state.loadingMore = false;
//         state.list = append ? [...state.list, ...sessions] : sessions;
//         state.pagination = pagination;
//       })
//       .addCase(fetchAllSessions.rejected, (state, action) => {
//         state.listState = 'error';
//         state.loading = false;
//         state.loadingMore = false;
//         state.error = action.payload;
//       })

//       // fetchSessionById
//       .addCase(fetchSessionById.pending, (state) => {
//         state.detailState = 'loading';
//         state.error = null;
//       })
//       .addCase(fetchSessionById.fulfilled, (state, action) => {
//         state.detailState = 'success';
//         state.selected = action.payload;
//       })
//       .addCase(fetchSessionById.rejected, (state, action) => {
//         state.detailState = 'error';
//         state.error = action.payload;
//       })

//       // rebuildSession
//       .addCase(rebuildSession.pending, (state) => {
//         state.detailState = 'loading';
//       })
//       .addCase(rebuildSession.fulfilled, (state, action) => {
//         state.detailState = 'success';
//         state.selected = action.payload;
//       })
//       .addCase(rebuildSession.rejected, (state, action) => {
//         state.detailState = 'error';
//         state.error = action.payload;
//       });
//   },
// });

// export const { clearSelectedSession, clearSessionError } = sessionSlice.actions;
// export default sessionSlice.reducer;
